/* ═══ EntityTypeahead — searchable picker backed by a public list endpoint ═══
 *
 * Every org/team picker used to be a plain <select> over a client-side array
 * that the API capped at 100 rows, alphabetically. Once the platform passed 100
 * clubs the lists just stopped mid-alphabet ("only going to the letter O") and
 * there was no way to reach anything after it.
 *
 * This searches server-side instead, so the reachable set is the whole table
 * regardless of size. It fetches on mount (so it's browsable with an empty box)
 * and re-fetches, debounced, as the user types.
 */
import React from "react";
import axios from "axios";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 250;

const EntityTypeahead = ({
  endpoint,                 // e.g. "/public/organizations"
  params = {},              // extra query params (league_id, org_id, …)
  value,                    // selected id
  onChange,                 // (id, item) => void
  placeholder = "Start typing to search…",
  emptyLabel = "No matches",
  subtitleFor,              // optional (item) => string shown under the name
  filterItem,               // optional (item) => bool, applied to fetched rows
  disabled = false,
  required = false,
  testid = "entity-typeahead",
}) => {
  const [query, setQuery] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const wrapRef = React.useRef(null);
  // The id this component last emitted. Lets the reset effect below tell an
  // EXTERNAL clear (the parent league changed) from the one we just caused by
  // typing — resetting on our own clear wiped the character the user typed.
  const emittedRef = React.useRef("");
  // Set while `query` holds a picked item's name rather than typed text, so
  // selecting doesn't kick off a search for the name we just filled in.
  const pickedRef = React.useRef(false);

  // params is typically an inline object literal, so it's a new reference on
  // every render — key the fetch off its contents, not its identity, or the
  // effect loops forever. (Only used as a dep; the request reads `params`.)
  const paramKey = JSON.stringify(params);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  React.useEffect(() => {
    if (value === emittedRef.current) return;   // our own change, already reflected
    emittedRef.current = value || "";
    if (!value) { pickedRef.current = false; setQuery(""); }
  }, [value]);

  React.useEffect(() => {
    if (disabled || pickedRef.current) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_ROOT}${endpoint}`, {
          params: { ...params, q: query.trim() || undefined, limit: 25 },
        });
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        setItems(filterItem ? rows.filter(filterItem) : rows);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query ? DEBOUNCE_MS : 0);
    return () => { cancelled = true; clearTimeout(timer); };
    // params is covered by paramKey; filterItem is usually an inline arrow, so
    // including it would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramKey, query, disabled]);

  const pick = (item) => {
    pickedRef.current = true;
    emittedRef.current = item.id;
    setQuery(item.name);
    setOpen(false);
    onChange(item.id, item);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }} data-testid={testid}>
      <input
        data-testid={`${testid}-input`}
        className="input-field"
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          pickedRef.current = false;
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
          if (value) { emittedRef.current = ""; onChange("", null); }
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, items.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" && items[highlight]) { e.preventDefault(); pick(items[highlight]); }
          else if (e.key === "Escape") setOpen(false);
        }}
        disabled={disabled}
        autoComplete="off"
        required={required}
      />
      {open && items.length > 0 && (
        <div
          data-testid={`${testid}-dropdown`}
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
            background: "#121216", border: "1px solid var(--dark-border)", borderRadius: 10,
            maxHeight: 220, overflowY: "auto", zIndex: 50,
            boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
          }}
        >
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              data-testid={`${testid}-option-${item.id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(item)}
              onMouseEnter={() => setHighlight(i)}
              style={{
                width: "100%", textAlign: "left", padding: "10px 14px",
                background: i === highlight ? "rgba(0,229,255,0.08)" : "transparent",
                border: "none", color: "var(--text-primary)", cursor: "pointer",
                fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <div style={{ fontWeight: 600 }}>{item.name}</div>
              {subtitleFor && subtitleFor(item) && (
                <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>{subtitleFor(item)}</div>
              )}
            </button>
          ))}
        </div>
      )}
      {open && !loading && items.length === 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
          background: "#121216", border: "1px solid var(--dark-border)", borderRadius: 10,
          padding: "10px 14px", fontSize: 13, color: "var(--text-secondary)", zIndex: 50,
        }}>
          {query ? `${emptyLabel} for "${query}"` : emptyLabel}
        </div>
      )}
    </div>
  );
};

export default EntityTypeahead;
